<script lang="ts">
  import type { ProviderType } from '$lib/services/providers/types'
  
  // Import provider icons  
  import bareunIcon from '../../../assets/bareun-big.png'
  import naraSpellerIcon from '../../../assets/nara-speller-icon.svg'
  
  interface GrammarProvider {
    id: ProviderType
    name: string
    icon: string
    description: string
    requiresServerUrl: boolean
    requiresApiKey: boolean
    defaultServerUrl?: string
    enabled: boolean
    apiKey?: string
    serverUrl?: string
    guideUrl?: string
  }
  
  interface Props {
    provider: GrammarProvider
    showApiKey?: boolean
    onToggle: () => void
    onUpdateApiKey: (value: string) => void
    onUpdateServerUrl?: (value: string) => void
    onToggleApiKeyVisibility: () => void
  }
  
  let {
    provider,
    showApiKey = false,
    onToggle,
    onUpdateApiKey,
    onUpdateServerUrl,
    onToggleApiKeyVisibility
  }: Props = $props()
  
  // Map provider IDs to imported icons
  const providerIcons: Record<string, string> = {
    bareun: bareunIcon,
    'bareun-custom': bareunIcon,
    naraSpeller: naraSpellerIcon
  }
  
  // Get the correct icon for the provider
  const providerIcon = $derived(providerIcons[provider.id] || provider.icon)
  
  function openProviderGuide() {
    if (provider.guideUrl) {
      window.open(provider.guideUrl, '_blank')
    }
  }
</script>

<div class="provider-card {provider.enabled ? 'enabled' : ''}">
  <div class="provider-header">
    <div class="provider-info">
      {#if providerIcon}
        <img 
          src={providerIcon} 
          alt="{provider.name} icon" 
          class="provider-icon-img {provider.id === 'naraSpeller' ? 'nara-icon' : ''}" 
        />
      {/if}
      <h4 class="provider-name">
        {provider.name}
        {#if provider.id === 'bareun'}
          <span class="custom-badge">사용자 정의 서버</span>
        {/if}
      </h4>
      {#if provider.guideUrl}
        <button
          type="button"
          class="guide-link"
          onclick={openProviderGuide}
          title="가이드 보기"
          aria-label="가이드 보기"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
            <polyline points="15 3 21 3 21 9"></polyline>
            <line x1="10" y1="14" x2="21" y2="3"></line>
          </svg>
        </button>
      {/if}
    </div>
    <label class="toggle-wrapper">
      <input
        type="checkbox"
        class="toggle-input"
        checked={provider.enabled}
        onchange={onToggle}
      />
      <span class="toggle-switch"></span>
    </label>
  </div>
  
  {#if provider.enabled}
    <div class="provider-config">
      {#if provider.requiresServerUrl}
        <div class="form-group">
          <label for="{provider.id}-url">서버 URL</label>
          <input
            id="{provider.id}-url"
            type="text"
            value={provider.serverUrl}
            oninput={(e) => onUpdateServerUrl?.(e.currentTarget.value)}
            placeholder="https://example.com/api"
            class="input"
          />
        </div>
      {/if}
      
      {#if provider.requiresApiKey}
        <div class="form-group">
          <label for="{provider.id}-key">API 키</label>
          <div class="input-group">
            <div class="input-with-icon">
              <input
                id="{provider.id}-key"
                type={showApiKey ? "text" : "password"}
                value={provider.apiKey}
                oninput={(e) => onUpdateApiKey(e.currentTarget.value)}
                placeholder="API 키를 입력하세요"
                class="input"
              />
              <button
                type="button"
                class="eye-toggle"
                onclick={onToggleApiKeyVisibility}
                title={showApiKey ? "숨기기" : "보기"}
              >
                {#if showApiKey}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                {:else}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                {/if}
              </button>
            </div>
          </div>
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  /* Provider Card - Match AI Settings design */
  .provider-card {
    padding: 20px 0;
    border-bottom: 1px solid var(--border-primary);
    transition: all 0.2s;
  }
  
  .provider-card:last-child {
    border-bottom: none;
  }
  
  .provider-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }
  
  .provider-info {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  
  .provider-icon-img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }
  
  /* Adjust NaraSpeller icon size to be smaller */
  .provider-icon-img.nara-icon {
    width: 20px;
    height: 20px;
  }
  
  /* No filter - show original logo colors */
  
  .provider-name {
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .custom-badge {
    padding: 2px 6px;
    background: var(--bg-tertiary);
    color: var(--text-secondary);
    border-radius: 4px;
    font-size: 10px;
    font-weight: 500;
    text-transform: none;
  }
  
  .guide-link {
    padding: 4px;
    background: none;
    border: none;
    color: var(--text-tertiary);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s;
  }
  
  .guide-link:hover {
    color: var(--accent-primary);
  }
  
  /* Toggle Switch - Original AI Settings styles */
  .toggle-wrapper {
    position: relative;
    display: inline-block;
  }
  
  .toggle-input {
    display: none;
  }
  
  .toggle-switch {
    display: block;
    width: 48px;
    height: 24px;
    background: var(--border-primary);
    border-radius: 12px;
    position: relative;
    cursor: pointer;
    transition: background 0.2s;
  }
  
  .toggle-switch::after {
    content: '';
    position: absolute;
    top: 3px;
    left: 3px;
    width: 18px;
    height: 18px;
    background: white;
    border-radius: 50%;
    transition: transform 0.2s;
  }
  
  .toggle-input:checked + .toggle-switch {
    background: var(--accent-primary);
  }
  
  .toggle-input:checked + .toggle-switch::after {
    transform: translateX(24px);
  }
  
  .provider-config {
    padding-top: 16px;
    border-top: 1px solid var(--bg-tertiary);
  }
  
  /* Form Styles - Match AI Settings */
  .form-group {
    margin-bottom: 16px;
  }
  
  .form-group:last-child {
    margin-bottom: 0;
  }
  
  label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: var(--text-primary);
    margin-bottom: 6px;
  }
  
  .input-group {
    display: flex;
    gap: 8px;
  }
  
  .input-with-icon {
    position: relative;
    flex: 1;
    display: flex;
  }
  
  .input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid var(--border-secondary);
    border-radius: 6px;
    font-size: 14px;
    background: var(--bg-primary);
    color: var(--text-primary);
    transition: all 0.2s;
  }
  
  .input-with-icon .input {
    padding-right: 40px;
  }
  
  .input:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
  }
  
  .eye-toggle {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: var(--text-secondary);
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .eye-toggle:hover {
    color: var(--text-primary);
  }

  /* Dark theme - using class selector to respect user's theme choice */
  :global(.dark-theme) .provider-config,
  :global(:root.dark-theme) .provider-config {
    border-top-color: #374151;
  }

  :global(.dark-theme) .input,
  :global(:root.dark-theme) .input {
    background: #2d2d2d;
    border-color: #444;
  }

  :global(.dark-theme) .input:focus,
  :global(:root.dark-theme) .input:focus {
    border-color: var(--accent-primary);
  }
</style>